'use client';

import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { DateTimeBlockDates } from './date-time-block-dates';
import { DateTimeBlockDay } from './date-time-block-day';
import { DateTimeBlockLabel } from './date-time-block-label';
import { DateTimeBlockRoot } from './date-time-block-root';
import { DateTimeBlockTimes } from './date-time-block-times';
import { DateTimeBlockWrap } from './date-time-block-wrap';
import type { DateTimeBlockPropsInterface } from './types/date-time-block.types';

/**
 * `DateTimeBlock` provides a shorthand interface for selecting dates and times.
 *
 * @since 10.4.0
 */
export const DateTimeBlock = forwardRef<HTMLDivElement, DateTimeBlockPropsInterface>(
  (props: DateTimeBlockPropsInterface, forwardedRef) => {
    const {
      dates,
      times,
      renderDatesPrevIconButton,
      renderDatesNextIconButton,
      renderTimesChips,
      renderTimesSelect,
      renderTimesHelp,
      ...rest
    } = props;
    const hasDates = dates && dates.length > 0;
    const hasTimes = times && times.length > 0;

    // Don't render anything if both dates and times are empty/undefined
    if (!hasDates && !hasTimes) {
      return null;
    }

    return (
      <DateTimeBlock.Root ref={forwardedRef} dates={dates} times={times} {...rest}>
        <DateTimeBlock.Wrap>
          <DateTimeBlock.Label />
        </DateTimeBlock.Wrap>
        {hasDates && (
          <DateTimeBlock.Dates
            renderDatesPrevIconButton={renderDatesPrevIconButton}
            renderDatesNextIconButton={renderDatesNextIconButton}
          />
        )}
        {hasTimes && (
          <DateTimeBlock.Wrap>
            <DateTimeBlock.Times
              renderTimesChips={renderTimesChips}
              renderTimesSelect={renderTimesSelect}
              renderTimesHelp={renderTimesHelp}
            />
          </DateTimeBlock.Wrap>
        )}
      </DateTimeBlock.Root>
    );
  },
) as ForwardRefExoticComponent<DateTimeBlockPropsInterface> & {
  Root: typeof DateTimeBlockRoot;
  Label: typeof DateTimeBlockLabel;
  Dates: typeof DateTimeBlockDates;
  Day: typeof DateTimeBlockDay;
  Times: typeof DateTimeBlockTimes;
  Wrap: typeof DateTimeBlockWrap;
};

DateTimeBlock.Root = DateTimeBlockRoot;
DateTimeBlock.Label = DateTimeBlockLabel;
DateTimeBlock.Dates = DateTimeBlockDates;
DateTimeBlock.Day = DateTimeBlockDay;
DateTimeBlock.Times = DateTimeBlockTimes;
DateTimeBlock.Wrap = DateTimeBlockWrap;

DateTimeBlock.displayName = 'DateTimeBlock';
